import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { AxiosResponse } from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Oval } from 'react-loader-spinner';
//@ts-ignore
import Modal from 'react-awesome-modal';

import AdminNavbar from '../Navbars/AdminNavbar';
import AdminSidebar from '../Sidebar/AdminSidebar';
import { SuperAdminApis } from '../../apis/superAdminApi';

type AccountType = 'Rider' | 'Fleet' | string;
type WalletStatus = 'active' | 'suspended' | 'frozen' | string;

interface WalletRow {
    id: number;
    user_id: number;
    available_balance: number | string;
    pending_balance: number | string;
    total_earned: number | string;
    total_withdrawn: number | string;
    status: WalletStatus;
    suspension_reason?: string | null;
    suspended_at?: string | null;
    updated_at?: string;
    account_type?: AccountType;
    user?: {
        id: number;
        first_name?: string;
        last_name?: string;
        email?: string;
        phone_number?: string;
        role?: string;
    };
}

const formatNaira = (amount: any) =>
    '₦' + Number(amount || 0).toLocaleString('en-NG', { minimumFractionDigits: 2 });

const statusBadgeClass = (s: string) => {
    switch (s) {
        case 'active':    return 'bg-green-100 text-green-700';
        case 'suspended': return 'bg-red-100 text-red-700';
        case 'frozen':    return 'bg-yellow-100 text-yellow-700';
        default:          return 'bg-gray-100 text-gray-700';
    }
};

const typeBadgeClass = (t: string) => {
    switch (t) {
        case 'Fleet': return 'bg-blue-100 text-blue-700';
        case 'Rider': return 'bg-purple-100 text-purple-700';
        default:      return 'bg-gray-100 text-gray-700';
    }
};

export default function AdminWallets() {
    const navigate = useNavigate();
    const userLoginData = useSelector((state: any) => state.data.login.value);

    // Permission flags (mirrors backend: admin/super_admin bypass)
    const { canViewWallets, canManageWallets } = useMemo(() => {
        const role = userLoginData?.data?.role;
        const isAdminRole = role === 'admin' || role === 'super_admin';
        let perms: string[] = [];
        try {
            perms = userLoginData?.data?.permissions
                ? JSON.parse(userLoginData.data.permissions)
                : [];
        } catch {
            perms = [];
        }
        return {
            canViewWallets:   isAdminRole || perms.includes('can_view_wallets'),
            canManageWallets: isAdminRole || perms.includes('can_manage_wallets'),
        };
    }, [userLoginData]);

    // List state
    const [wallets, setWallets] = useState<any>({ data: [] });
    const [loader, setLoader] = useState(false);

    // Stats
    const [stats, setStats] = useState<any>(null);

    // Filters
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState<'all' | WalletStatus>('all');
    const [typeFilter, setTypeFilter] = useState<'all' | 'rider' | 'fleet'>('all');
    // Where the balance sits. 'negative' is the one that matters operationally:
    // those accounts are carrying debt and cannot withdraw until they clear it.
    const [balanceFilter, setBalanceFilter] = useState<'all' | 'negative' | 'zero' | 'positive'>('all');

    // Suspend / reactivate modal
    const [actionModal, setActionModal] = useState(false);
    const [actionLoader, setActionLoader] = useState(false);
    const [activeWallet, setActiveWallet] = useState<WalletRow | null>(null);
    const [reason, setReason] = useState('');

    // Reset withdrawal PIN modal
    const [pinModal, setPinModal] = useState(false);
    const [pinLoader, setPinLoader] = useState(false);
    const [pinWallet, setPinWallet] = useState<WalletRow | null>(null);

    // CSV export
    const [exporting, setExporting] = useState(false);

    // ── Load wallets ─────────────────────────────────────────
    const loadWallets = useCallback((page?: string) => {
        if (!canViewWallets) return;
        setLoader(true);
        const params: any = {
            search: search || undefined,
            status: statusFilter !== 'all' ? statusFilter : undefined,
            type: typeFilter !== 'all' ? typeFilter : undefined,
            balance: balanceFilter !== 'all' ? balanceFilter : undefined,
            page: page || '1',
            per_page: '20',
        };

        SuperAdminApis.getAdminWallets(params)
            .then((res: AxiosResponse<any>) => {
                setLoader(false);
                if (res?.data?.status) {
                    setWallets(res.data.data);
                } else {
                    toast.error(res?.data?.message || 'Could not load wallets.');
                }
            })
            .catch((err) => {
                setLoader(false);
                if (err?.response?.status === 403) {
                    toast.error(err?.response?.data?.message || 'You do not have permission to view wallets.');
                } else {
                    toast.error('Failed to load wallets.');
                }
            });
    }, [search, statusFilter, typeFilter, balanceFilter, canViewWallets]);

    const loadStats = useCallback(() => {
        if (!canViewWallets) return;
        SuperAdminApis.getAdminWalletStats()
            .then((res: AxiosResponse<any>) => {
                if (res?.data?.status) setStats(res.data.data);
            })
            .catch(() => { /* silent — stats are non-critical */ });
    }, [canViewWallets]);

    useEffect(() => {
        loadWallets();
        loadStats();
    }, []); // initial load

    // ── Suspend / reactivate handlers ─────────────────────────
    const openActionModal = (w: WalletRow) => {
        if (!canManageWallets) {
            toast.error('You do not have permission to manage wallets.');
            return;
        }
        setActiveWallet(w);
        setReason('');
        setActionModal(true);
    };

    const closeActionModal = () => {
        setActionModal(false);
        setActiveWallet(null);
        setReason('');
    };

    const submitWalletAction = () => {
        if (!activeWallet) return;
        const isCurrentlyActive = activeWallet.status === 'active';

        if (isCurrentlyActive && !reason.trim()) {
            toast.error('Please provide a reason for suspending this wallet.');
            return;
        }

        setActionLoader(true);
        SuperAdminApis.toggleAdminWalletStatus(
            activeWallet.user_id,
            isCurrentlyActive ? { reason: reason.trim() } : undefined
        )
            .then((res: AxiosResponse<any>) => {
                setActionLoader(false);
                if (res?.data?.status) {
                    toast.success(res.data.message || 'Wallet status updated.');
                    closeActionModal();
                    loadWallets();
                    loadStats();
                } else {
                    toast.error(res?.data?.message || 'Could not update wallet status.');
                }
            })
            .catch((err: any) => {
                setActionLoader(false);
                toast.error(err?.response?.data?.message || 'Failed to update wallet status.');
            });
    };

    // ── Reset withdrawal PIN handlers ─────────────────────────
    // Clears the rider/fleet's withdrawal PIN (and any failed-attempt lockout)
    // so they can set a fresh one. System wallets have no rider profile, so the
    // trigger is hidden for them — see the Actions cell below.
    const openPinModal = (w: WalletRow) => {
        if (!canManageWallets) {
            toast.error('You do not have permission to manage wallets.');
            return;
        }
        setPinWallet(w);
        setPinModal(true);
    };

    const closePinModal = () => {
        setPinModal(false);
        setPinWallet(null);
    };

    const submitPinReset = () => {
        if (!pinWallet) return;

        setPinLoader(true);
        SuperAdminApis.resetWalletPin(pinWallet.user_id)
            .then((res: AxiosResponse<any>) => {
                setPinLoader(false);
                if (res?.data?.status) {
                    toast.success(res.data.message || 'Withdrawal PIN has been reset.');
                    closePinModal();
                } else {
                    toast.error(res?.data?.message || 'Could not reset the withdrawal PIN.');
                }
            })
            .catch((err: any) => {
                setPinLoader(false);
                if (err?.response?.status === 404) {
                    toast.error('This account has no rider profile, so it has no withdrawal PIN.');
                } else {
                    toast.error(err?.response?.data?.message || 'Failed to reset the withdrawal PIN.');
                }
            });
    };

    // ── CSV export ────────────────────────────────────────────
    const handleExport = useCallback(() => {
        if (!canViewWallets || exporting) return;
        setExporting(true);

        const params: any = {
            search: search || undefined,
            status: statusFilter !== 'all' ? statusFilter : undefined,
            type: typeFilter !== 'all' ? typeFilter : undefined,
            balance: balanceFilter !== 'all' ? balanceFilter : undefined,
        };

        SuperAdminApis.exportAdminWallets(params)
            .then((res: AxiosResponse<any>) => {
                const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
                const url = window.URL.createObjectURL(blob);

                const stamp = new Date()
                    .toISOString()
                    .replace(/[-:]/g, '')
                    .replace('T', '-')
                    .slice(0, 15);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `wallets-export-${stamp}.csv`);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);

                toast.success('Export downloaded.');
            })
            .catch((err: any) => {
                if (err?.response?.status === 403) {
                    toast.error('You do not have permission to export wallets.');
                } else {
                    toast.error('Failed to export wallets.');
                }
            })
            .finally(() => setExporting(false));
    }, [canViewWallets, exporting, search, statusFilter, typeFilter, balanceFilter]);

    // ── Pagination ────────────────────────────────────────────
    const paginate = (label: string) => {
        const links = wallets?.links || [];
        const target = links.find((l: any) => l.label === label);
        if (target?.url) {
            const page = target.url.split('page=')[1];
            if (page) loadWallets(page);
        }
    };

    // ── Render ────────────────────────────────────────────────
    if (!canViewWallets) {
        return (
            <>
                <AdminSidebar title="Wallets" />
                <div className="relative md:ml-64 bg-gray-100 min-h-screen">
                    <AdminNavbar title="Wallets" />
                    <div className="max-w-3xl mx-auto px-6 py-16 text-center">
                        <h2 className="text-xl font-semibold text-gray-800 mb-2">Permission required</h2>
                        <p className="text-gray-600">
                            You don't have permission to view wallets. Please contact an administrator
                            if you believe this is a mistake.
                        </p>
                    </div>
                </div>
            </>
        );
    }

    const rows: WalletRow[] = Array.isArray(wallets?.data) ? wallets.data : [];

    return (
        <>
            <AdminSidebar title="Wallets" />
            <div className="relative md:ml-64 bg-gray-100 min-h-screen">
                <AdminNavbar title="Wallets" />

                <div className="px-4 md:px-10 mx-auto w-full py-6">
                    {/* Page header */}
                    <div className="flex items-center justify-between mb-4">
                        <h1 className="text-lg md:text-xl font-semibold text-gray-800">
                            All Wallets
                        </h1>
                        <button
                            onClick={handleExport}
                            disabled={exporting}
                            className={`inline-flex items-center gap-2 text-sm font-medium px-4 py-2 rounded ${
                                exporting
                                    ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                                    : 'bg-emerald-600 hover:bg-emerald-700 text-white'
                            }`}
                            title="Download a CSV of all wallets matching the current filters"
                        >
                            {exporting ? (
                                <>
                                    <Oval color="#fff" secondaryColor="#fff" height={14} width={14} strokeWidth={5} />
                                    Exporting…
                                </>
                            ) : (
                                <>
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3" />
                                    </svg>
                                    Export CSV
                                </>
                            )}
                        </button>
                    </div>

                    {/* Stats */}
                    {stats && (
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                            <StatCard label="Total Wallets"      value={stats.total_wallets ?? 0} />
                            <StatCard label="Active"             value={stats.active_wallets ?? 0} />
                            <StatCard label="Suspended"          value={stats.suspended_wallets ?? 0} valueClass="text-red-600" />
                            <StatCard label="Available Balance"  value={formatNaira(stats.total_available_balance)} />
                            {/* Clicking this filters the table to exactly these wallets. */}
                            <StatCard
                                label="Owing (negative)"
                                value={formatNaira(stats.negative_exposure ?? 0)}
                                valueClass="text-orange-600"
                                hint={`${stats.negative_wallets ?? 0} wallet(s) — click to view`}
                                onClick={() => { setBalanceFilter('negative'); setTimeout(() => loadWallets(), 0); }}
                            />
                        </div>
                    )}

                    {/* Filters */}
                    <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                            <input
                                type="text"
                                placeholder="Search by name or email…"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && loadWallets()}
                                className="border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                            <select
                                value={typeFilter}
                                onChange={(e) => setTypeFilter(e.target.value as any)}
                                className="border border-gray-300 rounded px-3 py-2 text-sm bg-white"
                            >
                                <option value="all">All Types</option>
                                <option value="rider">Rider</option>
                                <option value="fleet">Fleet</option>
                            </select>
                            {/* Balance. Sorted worst-debt-first when Negative is chosen,
                                which is the ordering that makes the list useful. */}
                            <select
                                value={balanceFilter}
                                onChange={(e) => setBalanceFilter(e.target.value as any)}
                                className="border border-gray-300 rounded px-3 py-2 text-sm bg-white"
                            >
                                <option value="all">All Balances</option>
                                <option value="negative">Negative (owing)</option>
                                <option value="zero">Zero</option>
                                <option value="positive">In credit</option>
                            </select>
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value as any)}
                                className="border border-gray-300 rounded px-3 py-2 text-sm bg-white"
                            >
                                <option value="all">All Statuses</option>
                                <option value="active">Active</option>
                                <option value="suspended">Suspended</option>
                                <option value="frozen">Frozen</option>
                            </select>
                            <button
                                onClick={() => loadWallets()}
                                className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded"
                            >
                                Apply Filters
                            </button>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200 text-sm">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-3 text-left font-semibold text-gray-700">Name</th>
                                        <th className="px-4 py-3 text-left font-semibold text-gray-700">Type</th>
                                        <th className="px-4 py-3 text-left font-semibold text-gray-700">Email</th>
                                        <th className="px-4 py-3 text-right font-semibold text-gray-700">Available</th>
                                        <th className="px-4 py-3 text-right font-semibold text-gray-700">Pending</th>
                                        <th className="px-4 py-3 text-center font-semibold text-gray-700">Status</th>
                                        <th className="px-4 py-3 text-center font-semibold text-gray-700">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {loader && (
                                        <tr>
                                            <td colSpan={7} className="px-4 py-10 text-center">
                                                <Oval color="#2563eb" secondaryColor="#dbeafe" height={36} width={36} wrapperClass="inline-block" />
                                            </td>
                                        </tr>
                                    )}
                                    {!loader && rows.length === 0 && (
                                        <tr>
                                            <td colSpan={7} className="px-4 py-10 text-center text-gray-500">
                                                No wallets found.
                                            </td>
                                        </tr>
                                    )}
                                    {!loader && rows.map((w) => {
                                        const fullName = `${w.user?.first_name ?? ''} ${w.user?.last_name ?? ''}`.trim() || '—';
                                        return (
                                            <tr key={w.id} className="hover:bg-gray-50">
                                                <td className="px-4 py-3 text-gray-800">{fullName}</td>
                                                <td className="px-4 py-3">
                                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${typeBadgeClass(w.account_type || '')}`}>
                                                        {w.account_type || '—'}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 text-gray-600">{w.user?.email ?? '—'}</td>
                                                <td className="px-4 py-3 text-right font-medium text-gray-800">{formatNaira(w.available_balance)}</td>
                                                <td className="px-4 py-3 text-right text-gray-600">{formatNaira(w.pending_balance)}</td>
                                                <td className="px-4 py-3 text-center">
                                                    <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${statusBadgeClass(w.status)}`}>
                                                        {w.status}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 text-center space-x-2">
                                                    <button
                                                        onClick={() => navigate(`/admin/wallets/${w.user_id}`)}
                                                        className="text-blue-600 hover:text-blue-800 text-xs font-medium"
                                                    >
                                                        View
                                                    </button>
                                                    {canManageWallets && (
                                                        <button
                                                            onClick={() => openActionModal(w)}
                                                            className={`text-xs font-medium ${
                                                                w.status === 'active'
                                                                    ? 'text-red-600 hover:text-red-800'
                                                                    : 'text-green-600 hover:text-green-800'
                                                            }`}
                                                        >
                                                            {w.status === 'active' ? 'Suspend' : 'Reactivate'}
                                                        </button>
                                                    )}
                                                    {/* Only Rider/Fleet accounts have a rider_profile, and
                                                        therefore a withdrawal PIN. System wallets (e.g. Loan
                                                        Recovery) and staff roles have none. */}
                                                    {canManageWallets && (w.account_type === 'Rider' || w.account_type === 'Fleet') && (
                                                        <button
                                                            onClick={() => openPinModal(w)}
                                                            className="text-xs font-medium text-amber-600 hover:text-amber-800"
                                                        >
                                                            Reset PIN
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {wallets?.links && wallets.links.length > 3 && (
                            <div className="flex items-center justify-end gap-1 px-4 py-3 border-t border-gray-100">
                                {wallets.links.map((link: any, idx: number) => (
                                    <button
                                        key={idx}
                                        disabled={!link.url}
                                        onClick={() => paginate(link.label)}
                                        className={`px-3 py-1 rounded text-xs ${
                                            link.active
                                                ? 'bg-blue-600 text-white'
                                                : link.url
                                                    ? 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                                                    : 'bg-gray-50 text-gray-400 border border-gray-100 cursor-not-allowed'
                                        }`}
                                        dangerouslySetInnerHTML={{ __html: link.label }}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Suspend / Reactivate Modal */}
            <Modal visible={actionModal} width="480" effect="fadeInUp" onClickAway={closeActionModal}>
                {activeWallet && (
                    <div className="p-6">
                        <h3 className="text-lg font-semibold text-gray-800 mb-1">
                            {activeWallet.status === 'active' ? 'Suspend wallet' : 'Reactivate wallet'}
                        </h3>
                        <p className="text-sm text-gray-600 mb-4">
                            {activeWallet.user?.first_name} {activeWallet.user?.last_name} — {activeWallet.account_type || 'Wallet'}
                        </p>

                        {activeWallet.status === 'active' ? (
                            <>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Reason for suspension <span className="text-red-500">*</span>
                                </label>
                                <textarea
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    rows={4}
                                    placeholder="Explain why this wallet is being suspended…"
                                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-red-500 mb-1"
                                />
                                <p className="text-xs text-gray-500 mb-4">
                                    The rider/fleet will not be able to withdraw while suspended. This action is audited.
                                </p>
                            </>
                        ) : (
                            <p className="text-sm text-gray-700 mb-4">
                                Reactivating will re-enable withdrawals for this wallet. This action is audited.
                            </p>
                        )}

                        <div className="flex justify-end gap-2">
                            <button
                                onClick={closeActionModal}
                                disabled={actionLoader}
                                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={submitWalletAction}
                                disabled={actionLoader}
                                className={`px-4 py-2 text-sm font-medium text-white rounded flex items-center gap-2 ${
                                    activeWallet.status === 'active'
                                        ? 'bg-red-600 hover:bg-red-700'
                                        : 'bg-green-600 hover:bg-green-700'
                                } disabled:opacity-60`}
                            >
                                {actionLoader && <Oval color="#fff" secondaryColor="#f3f4f6" height={14} width={14} />}
                                {activeWallet.status === 'active' ? 'Suspend' : 'Reactivate'}
                            </button>
                        </div>
                    </div>
                )}
            </Modal>

            {/* Reset Withdrawal PIN Modal */}
            <Modal visible={pinModal} width="480" effect="fadeInUp" onClickAway={closePinModal}>
                {pinWallet && (
                    <div className="p-6">
                        <h3 className="text-lg font-semibold text-gray-800 mb-1">
                            Reset withdrawal PIN
                        </h3>
                        <p className="text-sm text-gray-600 mb-4">
                            {pinWallet.user?.first_name} {pinWallet.user?.last_name} — {pinWallet.account_type || 'Wallet'}
                        </p>

                        <p className="text-sm text-gray-700 mb-2">
                            This clears the existing withdrawal PIN so the user can create a fresh
                            one from their wallet. It also lifts any lockout from failed PIN attempts.
                        </p>
                        <p className="text-xs text-gray-500 mb-4">
                            They will not be able to withdraw until they set a new PIN. Only do this
                            once you have confirmed you are speaking to the account owner. This
                            action is audited.
                        </p>

                        <div className="flex justify-end gap-2">
                            <button
                                onClick={closePinModal}
                                disabled={pinLoader}
                                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={submitPinReset}
                                disabled={pinLoader}
                                className="px-4 py-2 text-sm font-medium text-white rounded flex items-center gap-2 bg-amber-600 hover:bg-amber-700 disabled:opacity-60"
                            >
                                {pinLoader && <Oval color="#fff" secondaryColor="#f3f4f6" height={14} width={14} />}
                                Reset PIN
                            </button>
                        </div>
                    </div>
                )}
            </Modal>

            <ToastContainer position="top-right" autoClose={4000} />
        </>
    );
}

function StatCard({ label, value, valueClass = '', onClick, hint }: { label: string; value: any; valueClass?: string; onClick?: () => void; hint?: string }) {
    // A figure you can click through to the rows behind it. Without this the
    // exposure total is a number with nothing to do about it.
    const body = (
        <>
            <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">{label}</p>
            <p className={`text-xl font-semibold text-gray-800 ${valueClass}`}>{value}</p>
            {hint && <p className="text-[11px] text-gray-400 mt-0.5">{hint}</p>}
        </>
    );

    if (!onClick) {
        return <div className="bg-white rounded-lg shadow-sm p-4">{body}</div>;
    }

    return (
        <button type="button" onClick={onClick}
            className="bg-white rounded-lg shadow-sm p-4 text-left w-full hover:ring-2 hover:ring-blue-300 transition">
            {body}
        </button>
    );
}
